import * as d3 from "d3";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import {
  useRefs,
  useResize,
  useScales,
  useStyles,
} from "@/components/organisms/DateTimeline/DateTimeline.hooks";
import {
  type DateTimelineAxisStyleType,
  type DateTimelineBrushPropsType,
  type DateTimelineChartPropsType,
  type DateTimelineGlobalBrushStyleType,
  type DateTimelineGridStyleType,
  type DateTimelineProps,
} from "@/components/organisms/DateTimeline/DateTimeline.types";
import { DateTimelineAxes } from "@/components/organisms/DateTimeline/DateTimelineAxes";
import { DateTimelineBrush } from "@/components/organisms/DateTimeline/DateTimelineBrush";
import { DateTimelineChartGrid } from "@/components/organisms/DateTimeline/DateTimelineChartGrid";
import { DateTimelineSeries } from "@/components/organisms/DateTimeline/DateTimelineSeries";
import { DateTimelineTooltip } from "@/components/organisms/DateTimeline/DateTimelineTooltip";

const EMPTY_OBJECT = {};

export function DateTimeline<T>({
  data,
  dateKey,
  series: seriesProp = [],
  chart: chartProps = EMPTY_OBJECT as DateTimelineChartPropsType,
  brush: brushProps = EMPTY_OBJECT as DateTimelineBrushPropsType,
  tooltip,
}: DateTimelineProps<T>) {
  const [isD3Initialized, setIsD3Initialized] = useState(false);
  // Series
  const seriesKey = useMemo(
    () =>
      seriesProp
        .map((s) => {
          if (s.type === "line") {
            return `${s.type}:${s.valueKey}`;
          }
          if (s.type === "bar") {
            return `${s.type}:${s.valueKeys.join(",")}`;
          }
          return "";
        })
        .join(";"),
    [seriesProp],
  );
  const series = useMemo(() => seriesProp, [seriesKey]);

  // Chart props
  const {
    height: chartHeightProp = 400,
    padding: chartPadding = 0.2,
    grid: gridProps = EMPTY_OBJECT as NonNullable<
      DateTimelineChartPropsType["grid"]
    >,
    xAxis: xAxisProps = EMPTY_OBJECT as NonNullable<
      DateTimelineChartPropsType["xAxis"]
    >,
    yAxis: yAxisProps = EMPTY_OBJECT as NonNullable<
      DateTimelineChartPropsType["yAxis"]
    >,
    isVisible: isChartVisible = true,
    isGraphZoomedOnBrushSelection = false,
  } = chartProps;

  // Brush props
  const {
    height: brushHeightProp = 60,
    onBrushChange,
    selectedRange: controlledSelectedRange,
    spacing: brushSpacing = isChartVisible ? 40 : 0,
    style: brushStyle = EMPTY_OBJECT as DateTimelineGlobalBrushStyleType,
    isVisible: isBrushVisible = true,
  } = brushProps;

  // Grid props
  const {
    isXVisible: isXGridVisible = true,
    isYVisible: isYGridVisible = true,
    style: gridStyle = EMPTY_OBJECT as DateTimelineGridStyleType,
  } = gridProps;

  // X axis props
  const {
    style: xAxisStyle = EMPTY_OBJECT as DateTimelineAxisStyleType,
    isVisible: isXAxisVisible = true,
  } = xAxisProps;

  // Y axis props
  const {
    style: yAxisStyle = EMPTY_OBJECT as DateTimelineAxisStyleType,
    isVisible: isYAxisVisible = true,
  } = yAxisProps;

  // Refs
  const {
    brushDateSelectionRef,
    chartContainerRef,
    chartGroupRef,
    containerRef,
    svgRef,
    isInitialized,
  } = useRefs();

  // Brush change ref
  const [uncontrolledSelectedRange, setUncontrolledSelectedRange] = useState<
    [Date, Date] | null
  >(null);
  const isBrushControlled = controlledSelectedRange !== undefined;

  const onBrushChangeRef = useRef(onBrushChange);
  useEffect(() => {
    onBrushChangeRef.current = onBrushChange;
  }, [onBrushChange]);

  const handleBrushChange = (extent: [Date, Date] | null) => {
    if (!isBrushControlled) {
      setUncontrolledSelectedRange(extent);
    }
    onBrushChangeRef.current?.(extent);
  };

  const handleBrushChangeRef = useRef(handleBrushChange);
  useEffect(() => {
    handleBrushChangeRef.current = handleBrushChange;
  });

  const selectedRange = isBrushControlled
    ? controlledSelectedRange
    : uncontrolledSelectedRange;

  // Dimensions & spacing
  const width = useResize(containerRef);
  const chartHeight = isChartVisible ? chartHeightProp : 0;
  const brushHeight = isBrushVisible ? brushHeightProp : 0;
  const margin = useMemo(
    () => ({
      top: isChartVisible ? 20 : 0,
      right: isChartVisible ? 20 : 0,
      bottom: isBrushVisible ? brushHeight + brushSpacing : 40,
      left: isChartVisible ? 60 : 0,
    }),
    [isBrushVisible, brushHeight, brushSpacing, isChartVisible],
  );
  const chartWidth = width - margin.left - margin.right;
  const totalHeight = useMemo(
    () => chartHeight + margin.top + margin.bottom,
    [chartHeight, margin],
  );

  const finalStyles = useStyles({
    series,
    brushStyle,
    gridStyle,
    xAxisStyle,
    yAxisStyle,
    isBrushVisible,
  });

  const { adjustedXScale, yScale, brushXScale, miniYScale } = useScales({
    data,
    dateKey,
    series,
    chartWidth,
    chartHeight,
    chartPadding,
    brushHeight,
    brushPadding: finalStyles.brushStyle.padding ?? 0,
    selectedRange,
    isGraphZoomedOnBrushSelection,
  });

  useEffect(() => {
    if (!svgRef.current || width === 0 || isD3Initialized) {
      return;
    }

    const svg = d3.select(svgRef.current);

    if (!chartContainerRef.current) {
      const chartContainer = svg.append("g").attr("class", "chart-container");
      chartContainerRef.current = chartContainer;
    }

    if (!chartGroupRef.current) {
      const g = chartContainerRef.current
        .append("g")
        .attr("class", "main-chart");
      chartGroupRef.current = g;
    }

    setIsD3Initialized(true);
  }, [width, isD3Initialized, chartContainerRef, chartGroupRef, svgRef]);

  useLayoutEffect(() => {
    if (!isD3Initialized || !chartContainerRef.current) {
      return;
    }
    chartContainerRef.current.attr(
      "transform",
      `translate(${margin.left},${margin.top})`,
    );
  }, [isD3Initialized, margin.left, margin.top]);

  const chartBarLikeSeriesIndices = new Map<number, number>();
  let currentChartBarLikeIndex = 0;
  series.forEach((s, i) => {
    if (s.type === "bar" && !s.isExcludedFromChart) {
      chartBarLikeSeriesIndices.set(i, currentChartBarLikeIndex);
      currentChartBarLikeIndex++;
    }
  });
  const numChartBarLikeSeries = chartBarLikeSeriesIndices.size;
  const chartSeriesBandwidth =
    adjustedXScale.bandwidth() / (numChartBarLikeSeries || 1);

  const id = useId();

  return (
    <div
      key={id}
      ref={containerRef}
      className="w-full h-(--height-3) relative"
      style={
        {
          "--height-3":
            Number(`${totalHeight}px`) <= 1
              ? `${Number(`${totalHeight}px`) * 100}%`
              : `${totalHeight}px`,
        } as CSSProperties
      }
    >
      <svg
        ref={svgRef}
        height={totalHeight}
        style={{
          display: "block",
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: "12px",
        }}
        width={width}
      >
        {isD3Initialized && isChartVisible && (
          <>
            <DateTimelineChartGrid
              chartGroup={chartGroupRef.current!}
              gridStyle={finalStyles.gridStyle}
              height={chartHeight}
              isXGridVisible={isXGridVisible}
              isYGridVisible={isYGridVisible}
              width={chartWidth}
              xScale={adjustedXScale}
              yScale={yScale}
            />
            <DateTimelineAxes
              chartGroup={chartGroupRef.current!}
              data={data}
              dateKey={dateKey}
              height={chartHeight}
              isXAxisVisible={isXAxisVisible}
              isYAxisVisible={isYAxisVisible}
              width={chartWidth}
              xAxisStyle={finalStyles.xAxisStyle}
              xScale={adjustedXScale}
              yAxisStyle={finalStyles.yAxisStyle}
              yScale={yScale}
            />
            <DateTimelineSeries
              chartBarLikeSeriesIndices={chartBarLikeSeriesIndices}
              chartGroup={chartGroupRef.current!}
              chartHeight={chartHeight}
              chartSeriesBandwidth={chartSeriesBandwidth}
              data={data}
              dateKey={dateKey}
              seriesStyles={finalStyles.seriesStyles}
              floatingGap={4}
              isInitialized={isInitialized}
              series={series}
              tooltip={tooltip}
              xScale={adjustedXScale}
              yScale={yScale}
              selectedRange={selectedRange}
            />
            {isD3Initialized && (
              <DateTimelineBrush
                adjustedXScale={adjustedXScale}
                brushDateSelectionRef={brushDateSelectionRef}
                brushHeight={brushHeight}
                brushSpacing={brushSpacing}
                brushXScale={brushXScale}
                chartContainer={chartContainerRef.current!}
                chartGroup={chartGroupRef.current!}
                chartHeight={chartHeight}
                data={data}
                dateKey={dateKey}
                finalStyles={finalStyles}
                isBrushControlled={isBrushControlled}
                isBrushVisible={isBrushVisible}
                isInitialized={isInitialized}
                margin={margin}
                miniYScale={miniYScale}
                onBrushChangeRef={handleBrushChangeRef}
                selectedRange={selectedRange}
                series={series}
                seriesStyles={finalStyles.seriesStyles}
                width={width}
                yScale={yScale}
              />
            )}
          </>
        )}
      </svg>
      {tooltip && (
        <DateTimelineTooltip
          adjustedXScale={adjustedXScale}
          data={data}
          dateKey={dateKey}
          chartHeight={chartHeight}
          containerRef={containerRef}
          margin={margin}
          svgRef={svgRef}
          tooltip={tooltip}
          width={width}
          series={series}
        />
      )}
    </div>
  );
}
